import { Card, CardContent } from '@sea-snag/ui';
import { Grid3X3 } from 'lucide-react';

import type { CalendarWeek } from '../lib/api-client';
import { type MedianCard, medianCard, positionNote, weekSummary } from '../lib/comp-set-summary';
import { formatCurrency, getPositionTextClass } from './comp-set-shared';

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

const cardValue = (card: MedianCard) =>
  card.median != null ? formatCurrency(card.median) : card.allBooked ? 'All booked' : '—';

/**
 * The summary cards above the market status: the selected week's median price
 * of all our boats in the set against the competitors' median, and the gap
 * between them (#258). The week is the one picked on the page. Boats booked
 * that week are left out of both medians: a booked week's price is not an
 * offer (D55).
 */
export function CompSetSummaryStrip({
  week,
  weekLabel,
  onOpenMatrix,
}: {
  week: CalendarWeek | null;
  /** The selected week, e.g. "Jun 27 – Jul 4". */
  weekLabel: string;
  onOpenMatrix?: () => void;
}) {
  if (!week) return null;
  const summary = weekSummary(week);
  const ours = medianCard(
    summary.ours,
    summary.oursCount,
    summary.oursBooked,
    (n) => `${n} of your boats`,
    weekLabel
  );
  const market = medianCard(
    summary.market,
    summary.marketCount,
    summary.marketBooked,
    (n) => plural(n, 'competitor', 'competitors'),
    weekLabel
  );

  const cols = onOpenMatrix ? 'lg:grid-cols-4' : 'lg:grid-cols-3';

  return (
    <div className={`grid grid-cols-2 gap-3 ${cols}`}>
      <SummaryCard label="Your median price" value={cardValue(ours)} subtitle={ours.subtitle} />
      <SummaryCard label="Market median" value={cardValue(market)} subtitle={market.subtitle} />
      <SummaryCard
        label="Your position"
        value={
          summary.positionPct != null
            ? `${summary.positionPct > 0 ? '+' : ''}${summary.positionPct.toFixed(1)}%`
            : '—'
        }
        valueClass={getPositionTextClass(summary.position)}
        subtitle={positionNote(summary)}
      />
      {onOpenMatrix && (
        <button
          type="button"
          onClick={onOpenMatrix}
          className="cursor-pointer rounded-xl text-left transition-shadow hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Open full season matrix"
        >
          <Card className="h-full transition-colors hover:bg-muted/40">
            <CardContent className="px-4 py-3">
              <p className="text-muted-foreground text-xs">Full Season Matrix</p>
              <p className="flex items-center gap-2 text-lg font-semibold">
                <Grid3X3 className="size-5 text-muted-foreground" />
                <span>Open</span>
              </p>
              <p className="text-xs text-muted-foreground">Per-boat pricing for every week</p>
            </CardContent>
          </Card>
        </button>
      )}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  valueClass,
  subtitle,
}: {
  label: string;
  value: string;
  valueClass?: string;
  subtitle?: string | undefined;
}) {
  return (
    <Card>
      <CardContent className="px-4 py-3">
        <p className="text-muted-foreground text-xs">{label}</p>
        <p className={`font-mono text-lg font-semibold tabular-nums ${valueClass ?? ''}`}>
          {value}
        </p>
        {subtitle && <p className="text-muted-foreground text-xs">{subtitle}</p>}
      </CardContent>
    </Card>
  );
}
